// noinspection JSUnusedGlobalSymbols

import QRCodeContext, {CreatePayloadNoKey} from "./QRCodeContext";
import {ReactNode, useContext, useEffect, useState} from "react";
import Page from "../../api/Page";
import SimpleQRCode from "../../api/qr-code/SimpleQRCode";
import QRCodeApi from "../../api/qr-code/QRCodeApi";
import GetListQuery from "../../api/qr-code/GetListQuery";
import Notifications from "../../utils/notifications/Notifications";
import AuthContext from "../auth/AuthContext";
import useFetching from "../../hooks/useFetching";
import QRCodeHelper from "../../helpers/qr-code/QRCodeHelper";
import CreatePayload from "../../api/qr-code/CreatePayload";

type WrapQRCodeContextProps = {
    /**
     * Children to be wrapped.
     * @author Kacper Faber
     * @since 0.1.7
     */
    children: ReactNode | ReactNode[] | null;

    /**
     * If set, system will use authentication context and does not reload if user is not authenticated.
     * @author Kacper Faber
     * @since 0.1.7
     */
    mustBeAuthenticated?: boolean;
};

const defaultQuery: GetListQuery = { page: 1, limit: 10 };

/**
 * Wraps your children inside a QRCodeContext provider.
 * @author Kacper Faber
 * @since 0.1.7
 * @constructor
 */
function WrapQRCodeContext(props: WrapQRCodeContextProps) {
    /**
     * Page of simple QR codes fetch from API.
     * It's possible undefined.
     * Will be reloaded, after query is updated.
     * @author Kacper Faber
     * @since 0.1.7
     */
    const [page, setPage] = useState<Page<SimpleQRCode>>();

    /**
     * Is actually fetching?
     * @author Kacper Faber
     * @since 0.1.7
     */
    const [isFetching, startFetching, stopFetching] = useFetching(false);

    const authContext = useContext(AuthContext);

    /**
     * Query to search QR codes.
     * @author Kacper Faber
     * @since 0.1.7
     */
    const [query, setQuery] = useState<GetListQuery>({...defaultQuery});

    /**
     * Reload data stored in page.
     * To use after query is changed.
     * Does nothing, if user is not authenticated and 'mustBeAuthenticated' is true.
     * @author Kacper Faber
     * @since 0.1.7
     */
    const reload = () => {
        if ((authContext.user === undefined) && (props.mustBeAuthenticated === true)) {
            return;
        }

        startFetching();

        QRCodeApi.getList(query)
            .then(setPage)
            .catch(Notifications.onFetchError)
            .finally(stopFetching);
    }

    /**
     * Find the QR code by ID
     * @since 0.1.9
     * @author Kacper Faber
     */
    const getById = (id: number | string) => {
        return QRCodeApi.getById(id);
    };

    /**
     * Delete QR code by ID
     * @author Kacper Faber
     * @since 0.1.9
     */
    const deleteById = async (id: number | string) => {
        await QRCodeApi.deleteById(id);
        reload();
    };

    /**
     * @author Kacper Faber
     * @since 0.2.1
     */
    const create = async (payload: CreatePayloadNoKey) => {
        const p: CreatePayload = { ...payload, key: QRCodeHelper.getRandomKey() };
        const image = await QRCodeHelper.generateSimpleFile(p);
        const qr = await QRCodeApi.create({...p, image});
        reload();
        return qr;
    }

    useEffect(reload, [query, authContext.user]);

    return (
        <QRCodeContext.Provider
            value={{ deleteById, page, query, setQuery, isFetching, getById, reload, create }}
            children={props.children}
        />
    );
}

export default WrapQRCodeContext;